import { useCallback, useEffect, useState } from "react";
import { Building2, ChevronsUpDown, FolderOpen } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  dashboardPathFor,
  isBuildingOnlyPath,
  isProjectsOnlyPath,
} from "@/components/layout/buildingViewRoutes";
import { ProjectPickerPanel } from "@/components/building/ProjectPickerPanel";
import { useCreateProjectDialog } from "@/domains/projects/components/createProjectDialogContext";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import { useBuildingViewMode } from "@/hooks/buildings/useBuildingViewMode";
import type { BuildingViewMode } from "@/hooks/buildings/useBuildingViewMode";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { SidebarTooltip } from "../atoms/SidebarTooltip";
import { SIDEBAR_MENU_ROWS } from "../atoms/sidebarMenuRows";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

interface SidebarViewModeToggleProps {
  buildingId: string;
  /** Whether the sidebar is collapsed (icon-only rendering). */
  isCollapsed: boolean;
  /** The building's name, shown above the project. Absent while its detail loads. */
  buildingName?: string;
  /**
   * A subcontractor-only member: Projects view is all they have, so there is
   * no Building choice and no "Whole building" row — only the project, which
   * opens the dropdown when there is more than one to switch between.
   */
  projectsOnly?: boolean;
}

/**
 * The building / project picker rendered above the sidebar nav.
 *
 * Expanded, it reads as a heading: the building's name (pressing it is
 * Building view) over a "Project" field naming the scoped project — or "Whole
 * building" — whose chevron opens the project dropdown. That dropdown also
 * offers "Whole building", so either view can be reached from it. Collapsed,
 * the same two choices are a vertical pair of icon chips, the active one
 * tinted and marked with an edge bar like the selected nav item below it.
 *
 * Switching modes swaps the nav item set and how pages scope their data
 * (whole building vs one specific project). On switch:
 *  - if the current page doesn't exist in the target mode, navigate to that
 *    mode's dashboard;
 *  - switching to Projects opens the project dropdown anchored to
 *    the toggle (unless the building has exactly one project, which is
 *    entered directly) — Projects view works within one concrete project and the
 *    previous choice is deliberately not remembered, so the mode only flips
 *    once a project is picked (or immediately when the building has no
 *    projects at all, landing on the create-first empty state);
 *  - re-clicking the active Projects half re-opens the dropdown to switch
 *    projects — in both layouts, since the collapsed switch's two halves are
 *    buttons carrying their own mode just like the expanded pill's halves;
 *  - switching to Building on a shared page strips the `?project` param
 *    immediately so the page never renders one frame still project-scoped
 *    (`useBuildingScope` then forgets the scope entirely).
 */
export const SidebarViewModeToggle = observer(function SidebarViewModeToggle({
  buildingId,
  isCollapsed,
  buildingName,
  projectsOnly = false,
}: SidebarViewModeToggleProps) {
  const { t } = useLanguage();
  const { viewMode, setViewMode } = useBuildingViewMode(buildingId);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    selectedProjectId,
    setProject,
    projectOptions,
    optionsLoaded,
    optionsError,
    reloadOptions,
  } = useBuildingScope(buildingId);
  const { openCreateProject, canCreateProjects } = useCreateProjectDialog();
  const [pickerOpen, setPickerOpen] = useState(false);

  const enterProjectsMode = useCallback(() => {
    setViewMode("projects");
    if (isBuildingOnlyPath(location.pathname, buildingId)) {
      navigate(dashboardPathFor("projects", buildingId));
    }
    // Only reached when there is no project to pick — shared pages stay put
    // and render Projects view's create-first empty state.
  }, [setViewMode, location.pathname, buildingId, navigate]);

  const handleChange = (next: BuildingViewMode) => {
    if (next === viewMode) {
      // Already in Projects view: re-open the dropdown to switch projects.
      // Available in either layout — this is the only switcher.
      if (next === "projects") setPickerOpen(true);
      return;
    }

    if (next === "projects") {
      if (optionsLoaded && projectOptions.length === 0) {
        // Nothing to pick — Projects view shows the create-first empty state.
        enterProjectsMode();
        return;
      }
      if (optionsLoaded && projectOptions.length === 1) {
        // Nothing to choose between — go straight into the only project.
        handlePickProject(projectOptions[0].id);
        return;
      }
      // Always pick a project from the dropdown before the mode flips — the
      // previous selection is deliberately not remembered.
      setPickerOpen(true);
      return;
    }

    setViewMode(next);
    if (isProjectsOnlyPath(location.pathname, buildingId)) {
      navigate(dashboardPathFor("building", buildingId));
      return;
    }
    if (searchParams.has("project")) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("project");
      setSearchParams(nextParams, { replace: true });
    }
  };

  const handlePickProject = (projectId: number) => {
    setPickerOpen(false);
    // Entering Projects view, landing on its dashboard from a building-only
    // route, and dropping a stale `folderId` are all `setProject`'s job — it
    // reads the live location through a ref, which is what this callback needs:
    // the create-project dialog captured it when it opened, so the route it
    // acts on is the one it is called on, not the one current back then.
    setProject(projectId);
  };

  // "New project" from the dropdown: close the popover first so the shared
  // dialog is never a child of it. Entering Projects view on the project that
  // gets created is the provider's follow-through — the same `setProject`
  // handlePickProject runs for an existing project, taken from the layout so
  // it cannot be left holding a stale location.
  const handleCreateProject = () => {
    setPickerOpen(false);
    openCreateProject();
  };

  // If the options finish loading empty while the dropdown is open (the user
  // clicked before the load resolved), there is nothing to pick — fall
  // through to Projects view, which shows the create-first empty state.
  // Likewise a single project resolves the pick by itself — but only while
  // ENTERING Projects view. Re-opening the dropdown from the active Projects
  // half is a deliberate request for it (to switch, or for "New project").
  const soleProjectId = projectOptions.length === 1 ? projectOptions[0].id : null;
  useEffect(() => {
    if (!pickerOpen || !optionsLoaded || projectOptions.length > 1) return;
    if (soleProjectId != null) {
      if (viewMode === "projects") return;
      setPickerOpen(false);
      setProject(soleProjectId);
      return;
    }
    setPickerOpen(false);
    enterProjectsMode();
  }, [
    pickerOpen,
    optionsLoaded,
    projectOptions.length,
    soleProjectId,
    viewMode,
    setProject,
    enterProjectsMode,
  ]);

  // "Whole building" in the dropdown: Building view, through the same path as
  // the building row and the Building chip.
  const handlePickWholeBuilding = () => {
    setPickerOpen(false);
    handleChange("building");
  };

  const groupLabel = `${t.buildings.sidebar.viewBuilding} / ${t.buildings.sidebar.viewProjects}`;
  const projectName =
    viewMode === "projects"
      ? projectOptions.find((project) => project.id === selectedProjectId)?.name
      : undefined;
  // What the Project field says in Projects view when it has no name to show.
  // Nothing scoped is not a failure — the page is asking for a pick, or for a
  // first project — so only a scoped project that can't be named (the options
  // failed, or it isn't among them) reads as unavailable. A load still in
  // flight holds the line blank, so it never flashes "Whole building" first.
  const missingProjectLine = (): string => {
    if (selectedProjectId == null) {
      if (optionsLoaded && projectOptions.length === 0) return t.buildings.sidebar.noProjectsYet;
      return t.buildings.sidebar.chooseProject;
    }
    return optionsLoaded || optionsError ? t.buildings.sidebar.projectUnavailable : "\u00a0";
  };
  // A projects-only member with a single project has nothing to switch to, so
  // the project reads as a label rather than a control.
  const canSwitchProject = !projectsOnly || projectOptions.length > 1;
  const projectLine =
    viewMode !== "projects"
      ? t.buildings.sidebar.wholeBuilding
      : (projectName ?? missingProjectLine());

  // The project dropdown, anchored to the picker and opening into the
  // content area beside it — used both for the first selection and for
  // switching projects. Its body is the shared panel, which owns the
  // loading and load-failed branches.
  const pickerContent = (
    <PopoverContent
      side="right"
      align="start"
      sideOffset={12}
      className={cn("w-[280px] p-0", SIDEBAR_MENU_ROWS)}
    >
      <ProjectPickerPanel
        projects={projectOptions}
        selectedProjectId={viewMode === "projects" ? selectedProjectId : null}
        optionsLoaded={optionsLoaded}
        optionsError={optionsError}
        onReload={reloadOptions}
        onSelect={handlePickProject}
        onCreateProject={canCreateProjects ? handleCreateProject : undefined}
        onSelectWholeBuilding={projectsOnly ? undefined : handlePickWholeBuilding}
        isWholeBuildingSelected={viewMode === "building"}
      />
    </PopoverContent>
  );

  if (isCollapsed) {
    // Two icon chips stacked vertically, building over project. The active
    // one is tinted and carries a bar on the sidebar's edge, echoing the
    // expanded picker's red rule. Each chip is its OWN button carrying its own
    // mode — pressing the active Projects chip re-opens the project dropdown,
    // which is the only way to switch project while collapsed. Each chip's
    // tooltip says what it is and names what it points at — the building, or
    // the building and project — since the collapsed sidebar shows neither.
    const allChips: Array<{ value: BuildingViewMode; label: string; icon: typeof Building2 }> = [
      { value: "building", label: t.buildings.sidebar.viewBuilding, icon: Building2 },
      { value: "projects", label: t.buildings.sidebar.viewProjects, icon: FolderOpen },
    ];
    const chips = projectsOnly ? allChips.filter((chip) => chip.value === "projects") : allChips;
    return (
      <Popover open={pickerOpen} onOpenChange={setPickerOpen} modal={true}>
        <PopoverAnchor asChild>
          <div
            role="group"
            aria-label={groupLabel}
            className="flex shrink-0 flex-col items-center gap-1 border-b pb-2"
            {...pageInfoTarget("layout.viewMode")}
          >
            {chips.map((chip) => {
              const isActive = viewMode === chip.value;
              const tooltip = (
                <span className="flex max-w-[12rem] flex-col">
                  <span className="text-xs text-muted-foreground">{chip.label}</span>
                  {buildingName && <span className="truncate">{buildingName}</span>}
                  {/* Only in Projects view: in Building view the field would say
                      "Whole building", which is not a project. */}
                  {chip.value === "projects" && viewMode === "projects" && (
                    <span className={cn("truncate", projectName && "text-primary")}>
                      {projectLine}
                    </span>
                  )}
                </span>
              );
              return (
                <div key={chip.value} className="relative">
                  {isActive && (
                    <span
                      aria-hidden="true"
                      className="absolute inset-y-1 -left-2.5 w-0.5 rounded-full bg-primary"
                    />
                  )}
                  <SidebarTooltip content={tooltip}>
                    {!canSwitchProject ? (
                      // Nothing to switch to, so not a button — but still a tab
                      // stop, so the keyboard reaches the tooltip, and named in
                      // text, which every screen reader reads.
                      <span
                        tabIndex={0}
                        className={cn(
                          "flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        )}
                      >
                        <chip.icon
                          aria-hidden="true"
                          className="h-[1.125rem] w-[1.125rem] shrink-0"
                        />
                        <span className="sr-only">
                          {[chip.label, buildingName, projectLine].filter(Boolean).join(", ")}
                        </span>
                      </span>
                    ) : (
                      <button
                        type="button"
                        aria-label={chip.label}
                        aria-pressed={isActive}
                        aria-haspopup={chip.value === "projects" ? "dialog" : undefined}
                        aria-expanded={chip.value === "projects" ? pickerOpen : undefined}
                        onClick={() => handleChange(chip.value)}
                        className={cn(
                          "flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          isActive
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                        )}
                      >
                        <chip.icon className="h-[1.125rem] w-[1.125rem] shrink-0" />
                      </button>
                    )}
                  </SidebarTooltip>
                </div>
              );
            })}
          </div>
        </PopoverAnchor>
        {pickerContent}
      </Popover>
    );
  }

  // The Project field: the project icon in the building icon's column, then
  // the label and the project's name behind the rule that marks Projects view.
  const projectRowClass =
    "mt-1 flex w-full items-center gap-2 rounded-md py-1 pl-2 pr-1.5 text-left";
  const projectField = (
    <>
      <FolderOpen
        className={cn(
          "h-4 w-4 shrink-0",
          viewMode === "projects" ? "text-primary" : "text-muted-foreground"
        )}
      />
      <span
        className={cn(
          "flex min-w-0 flex-1 flex-col border-l-2 pl-2.5",
          viewMode === "projects" ? "border-primary" : "border-border"
        )}
      >
        <span className="text-xs leading-4 text-muted-foreground">
          {t.buildings.sidebar.projectLabel}
        </span>
        <span
          className={cn(
            "truncate text-[0.9375rem] font-medium leading-5",
            projectName ? "text-primary" : "text-foreground"
          )}
        >
          {projectLine}
        </span>
      </span>
    </>
  );

  return (
    <Popover open={pickerOpen} onOpenChange={setPickerOpen} modal={true}>
      <div role="group" aria-label={groupLabel} {...pageInfoTarget("layout.viewMode")}>
        {/* No aria-labels on these two: the visible text — the building's name,
            and "Project" plus the project's name — is the accessible name, so
            a screen reader hears what is selected, not just the control. */}
        {projectsOnly ? (
          <div className="flex h-9 w-full items-center gap-2 px-2">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate text-[0.9375rem] font-semibold">
              {buildingName ?? t.buildings.sidebar.viewBuilding}
            </span>
          </div>
        ) : (
          <button
            type="button"
            aria-pressed={viewMode === "building"}
            onClick={() => handleChange("building")}
            className={cn(
              "flex h-9 w-full items-center gap-2 rounded-md px-2 text-left",
              "transition-colors hover:bg-foreground/5",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            )}
          >
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate text-[0.9375rem] font-semibold">
              {buildingName ?? t.buildings.sidebar.viewBuilding}
            </span>
          </button>
        )}
        <PopoverAnchor asChild>
          {canSwitchProject ? (
            // No aria-pressed: it opens a dialog rather than toggling, and the
            // name ("Project Whole building" / "Project <name>") already says
            // what is scoped.
            <button
              type="button"
              aria-haspopup="dialog"
              aria-expanded={pickerOpen}
              onClick={() => handleChange("projects")}
              {...pageInfoTarget("layout.projectSwitcher")}
              className={cn(
                projectRowClass,
                "transition-colors hover:bg-foreground/5",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              )}
            >
              {projectField}
              <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ) : (
            <div className={projectRowClass}>{projectField}</div>
          )}
        </PopoverAnchor>
      </div>
      {pickerContent}
    </Popover>
  );
});
